import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, AlertCircle, RefreshCw, CheckCircle, XCircle, Activity, Target, Zap, Calendar, RotateCcw, Wifi, WifiOff } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// Color mapping for different event types
const getEventColor = (eventType) => {
  const colors = {
    'bid_response': '#3B82F6',    // Blue
    'auction_end': '#10B981',     // Green  
    'auction_start': '#F59E0B',   // Amber
    'bid_won': '#8B5CF6',         // Purple
    'bid_timeout': '#EF4444',     // Red
    'config_update': '#6B7280',   // Gray
    'config_deploy': '#EC4899'    // Pink
  };
  return colors[eventType] || '#9CA3AF';
};

const AnalyticsTab = ({ api, currentSite }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    health: null,
    overview: null,
    performance: null,
    tracking: null,
    retries: null
  });
  const [selectedSite, setSelectedSite] = useState(currentSite || 'lovedeco');
  const [dateRange, setDateRange] = useState('24h');
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [healthResult, overviewResult, performanceResult, trackingResult, retriesResult] = await Promise.all([
        api.fetchAnalytics('health'),
        api.fetchAnalytics('overview', { siteId: selectedSite, dateRange }),
        api.fetchAnalytics('performance', { siteId: selectedSite, dateRange }),
        api.fetchAnalytics('tracking', { siteId: selectedSite, dateRange }),
        api.fetchAnalytics('retries', { siteId: selectedSite, dateRange })
      ]);
      
      setData({
        health: healthResult,
        overview: overviewResult,
        performance: performanceResult,
        tracking: trackingResult,
        retries: retriesResult
      });
      
      if (!healthResult.data || healthResult.data.length === 0) {
        setError('Analytics not configured. Check CF_ACCOUNT_ID, ANALYTICS_API_TOKEN, and PREBID_ANALYTICS binding.');
      }
    } catch (err) {
      setError('Failed to fetch analytics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentSite && currentSite !== selectedSite) {
      setSelectedSite(currentSite);
    }
  }, [currentSite]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedSite, dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
        <button 
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 size={24} />
          Analytics Dashboard
        </h2>
        
        <div className="flex gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="lovedeco">Love Deco</option>
            <option value="b365">B365</option>
            <option value="testnews">Test News</option>
          </select>
          
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {data.health?.data && data.health.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Events */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Events</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.health.data[0]?.total_events || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Bid Responses */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bid Responses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.overview?.data?.find(item => item.event_type === 'bid_response')?.event_count || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Winning Bids */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Winning Bids</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.overview?.data?.find(item => item.event_type === 'bid_won')?.event_count || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Win Rate */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Win Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const bidResponses = data.overview?.data?.find(item => item.event_type === 'bid_response')?.event_count || 0;
                    const bidWins = data.overview?.data?.find(item => item.event_type === 'bid_won')?.event_count || 0;
                    const winRate = bidResponses > 0 ? ((bidWins / bidResponses) * 100).toFixed(1) : 0;
                    return `${winRate}%`;
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Status */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          {data.health?.data && data.health.data.length > 0 ? (
            <CheckCircle className="text-green-500" size={20} />
          ) : (
            <XCircle className="text-red-500" size={20} />
          )}
          Analytics Health
        </h3>
        <p className="text-gray-600">
          Status: {data.health?.data && data.health.data.length > 0 ? 'Configured and Ready' : 'Not Configured'}
        </p>
        {data.health?.data && data.health.data.length > 0 && (
          <p className="text-sm text-green-600 mt-1">
            Total events: {data.health.data[0]?.total_events}
          </p>
        )}
        <p className="text-sm text-gray-500 mt-2">
          Last checked: {data.health?.timestamp}
        </p>
      </div>

      {/* Initialization Funnel Analysis */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="text-blue-500" size={20} />
          Initialization Funnel ({selectedSite} - {dateRange})
        </h3>
        {data.tracking?.data && data.tracking.data.length > 0 ? (
          <div className="space-y-4">
            {/* Funnel Steps */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {data.tracking.data.map((step, index) => {
                const isDropOff = index > 0 && data.tracking.data[index-1] && 
                  parseInt(step.count) < parseInt(data.tracking.data[index-1].count);
                const dropOffPercent = index > 0 && data.tracking.data[index-1] ? 
                  (100 - (parseInt(step.count) / parseInt(data.tracking.data[index-1].count) * 100)).toFixed(1) : 0;
                
                return (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {parseInt(step.count).toLocaleString()}
                    </div>
                    <div className="text-sm font-medium text-gray-600 mt-1">
                      {step.step_name.replace('_', ' ').toUpperCase()}
                    </div>
                    {isDropOff && (
                      <div className="text-xs text-red-600 mt-1">
                        -{dropOffPercent}% drop-off
                      </div>
                    )}
                    {index === 0 && (
                      <div className="text-xs text-green-600 mt-1">
                        100% baseline
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Success Rate Summary */}
            {data.tracking.data.length >= 2 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Success Rate Analysis</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {data.tracking.data[0] && data.tracking.data[data.tracking.data.length - 1] && (
                    <div>
                      <span className="text-blue-600 font-medium">Overall Success Rate:</span>
                      <div className="text-lg font-bold text-blue-900">
                        {((parseInt(data.tracking.data[data.tracking.data.length - 1].count) / 
                           parseInt(data.tracking.data[0].count)) * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-blue-600 font-medium">Total Pageviews:</span>
                    <div className="text-lg font-bold text-blue-900">
                      {data.tracking.data[0] ? parseInt(data.tracking.data[0].count).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium">Lost Users:</span>
                    <div className="text-lg font-bold text-red-600">
                      {data.tracking.data[0] && data.tracking.data[data.tracking.data.length - 1] ? 
                        (parseInt(data.tracking.data[0].count) - 
                         parseInt(data.tracking.data[data.tracking.data.length - 1].count)).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No tracking data available for {selectedSite}.</p>
            <p className="text-sm mt-2">Make sure tracker.js is deployed and collecting data.</p>
          </div>
        )}
      </div>

      {/* Script Loading Reliability Analysis */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RotateCcw className="text-orange-500" size={20} />
          Script Loading Reliability ({selectedSite} - {dateRange})
        </h3>
        {data.retries?.data && data.retries.data.length > 0 ? (
          <div className="space-y-6">
            {/* Retry KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(() => {
                const retryAttempts = data.retries.data.find(item => item.retry_event_type === 'script_retry_attempt');
                const retryExhausted = data.retries.data.find(item => item.retry_event_type === 'script_retry_exhausted');
                const totalAttempts = parseInt(retryAttempts?.event_count || 0);
                const totalExhausted = parseInt(retryExhausted?.event_count || 0);
                const successRate = totalAttempts > 0 ? (((totalAttempts - totalExhausted) / totalAttempts) * 100).toFixed(1) : 0;
                
                return (
                  <>
                    {/* Retry Success Rate */}
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                      <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Wifi className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Retry Success Rate</p>
                          <p className="text-2xl font-bold text-green-600">
                            {successRate}%
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Network issues resolved
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Total Retry Attempts */}
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
                      <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <RotateCcw className="h-6 w-6 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Retry Attempts</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {totalAttempts}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Network timeouts detected
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Failed After Retries */}
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
                      <div className="flex items-center">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <WifiOff className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Failed After Retries</p>
                          <p className="text-2xl font-bold text-red-600">
                            {totalExhausted}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Persistent network issues
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Retry Analysis Summary */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-semibold text-orange-900 mb-3">Network Timeout Retry Analysis</h4>
              <div className="space-y-4">
                {data.retries.data.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        item.retry_event_type === 'script_retry_attempt' 
                          ? 'bg-orange-100' 
                          : 'bg-red-100'
                      }`}>
                        {item.retry_event_type === 'script_retry_attempt' ? (
                          <RotateCcw className={`h-4 w-4 ${
                            item.retry_event_type === 'script_retry_attempt' 
                              ? 'text-orange-600' 
                              : 'text-red-600'
                          }`} />
                        ) : (
                          <WifiOff className={`h-4 w-4 ${
                            item.retry_event_type === 'script_retry_attempt' 
                              ? 'text-orange-600' 
                              : 'text-red-600'
                          }`} />
                        )}
                      </div>
                      <div>
                        <div className="font-medium capitalize">
                          {item.retry_event_type.replace('script_', '').replace('_', ' ')}
                        </div>
                        <div className="text-sm text-gray-600">
                          Error Type: {item.error_type || 'Network Timeout'}
                          {item.script_name && item.script_name !== 'unknown' && (
                            <span> • Script: {item.script_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {parseInt(item.event_count).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        events
                      </div>
                      {item.avg_attempts > 0 && (
                        <div className="text-xs text-gray-500">
                          Avg attempts: {parseFloat(item.avg_attempts).toFixed(1)}
                        </div>
                      )}
                      {item.avg_delay_ms > 0 && (
                        <div className="text-xs text-gray-500">
                          Avg delay: {parseInt(item.avg_delay_ms)}ms
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reliability Impact Summary */}
              {(() => {
                const retryAttempts = data.retries.data.find(item => item.retry_event_type === 'script_retry_attempt');
                const retryExhausted = data.retries.data.find(item => item.retry_event_type === 'script_retry_exhausted');
                const totalAttempts = parseInt(retryAttempts?.event_count || 0);
                const totalExhausted = parseInt(retryExhausted?.event_count || 0);
                const recoveredConnections = totalAttempts - totalExhausted;

                return totalAttempts > 0 ? (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-orange-600">{totalAttempts}</div>
                        <div className="text-sm text-gray-600">Network Issues Detected</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">{recoveredConnections}</div>
                        <div className="text-sm text-gray-600">Connections Recovered</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          {totalAttempts > 0 ? ((recoveredConnections / totalAttempts) * 100).toFixed(1) : 0}%
                        </div>
                        <div className="text-sm text-gray-600">Recovery Rate</div>
                      </div>
                    </div>
                    <div className="mt-3 text-center text-sm text-gray-600">
                      <strong>Impact:</strong> The retry mechanism successfully recovered {recoveredConnections} network connections
                      that would have resulted in failed ad serving without header bidding revenue.
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Wifi size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No retry events detected for {selectedSite}.</p>
            <p className="text-sm mt-2">This indicates excellent network stability or the retry mechanism hasn't been triggered yet.</p>
          </div>
        )}
      </div>

      {/* Event Overview Chart */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Event Distribution ({selectedSite})</h3>
        </div>
        <div className="p-6">
          {data.overview?.data && data.overview.data.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Donut Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.overview.data.map(item => ({
                        name: item.event_type.replace('_', ' ').toUpperCase(),
                        value: parseInt(item.event_count),
                        color: getEventColor(item.event_type)
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {data.overview.data.map((item, index) => (
                        <Cell key={`cell-${index}`} fill={getEventColor(item.event_type)} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [value.toLocaleString(), 'Events']}
                      labelStyle={{ color: '#374151' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Event Summary List */}
              <div className="space-y-3">
                {data.overview.data.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: getEventColor(item.event_type) }}
                      />
                      <span className="font-medium capitalize">
                        {item.event_type.replace('_', ' ')}
                      </span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">
                      {parseInt(item.event_count).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No event data available for {selectedSite}.</p>
          )}
        </div>
      </div>

      {/* Bidder Performance Chart */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Bidder Performance ({selectedSite})</h3>
        </div>
        <div className="p-6">
          {data.performance?.data && data.performance.data.length > 0 ? (
            <div className="space-y-6">
              {/* Bar Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.performance.data.map(bidder => ({
                      bidder: bidder.bidder.toUpperCase(),
                      avg_cpm: parseFloat(bidder.avg_cpm || 0),
                      bid_count: parseInt(bidder.bid_count || 0)
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="bidder" 
                      stroke="#6B7280"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                      formatter={(value, name) => [
                        name === 'avg_cpm' ? `€${value.toFixed(2)}` : value.toLocaleString(),
                        name === 'avg_cpm' ? 'Average CPM' : 'Bid Count'
                      ]}
                    />
                    <Legend />
                    <Bar 
                      dataKey="avg_cpm" 
                      fill="#3B82F6" 
                      name="Average CPM (€)" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Performance Summary Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Bidder</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Average CPM</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Bid Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.performance.data.map((bidder, index) => {
                      const avgCpm = parseFloat(bidder.avg_cpm || 0);
                      const bidCount = parseInt(bidder.bid_count || 0);
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">
                            {bidder.bidder}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="font-semibold text-green-600">
                              €{avgCpm.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {bidCount.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No bidder data available for {selectedSite}.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;